import { forwardRef, CSSProperties } from 'react'

import { tv, type VariantProps } from 'tailwind-variants'

import { cn } from '@/utils'

// Type definition compatible with the Color object from 'color' library
// This matches what createBrandColor returns from utils/colors
interface TColorProps {
  color: {
    darken(amount: number): { hex(): string }
  }
  hex: string
  isDark: boolean
  hasLowContrastToWhite: boolean
  hasCustomBrandColor: boolean
}

export const buttonVariants = tv({
  slots: {
    containerSlot:
      'font-body disabled:border-surface-white-300 disabled:bg-surface-white-50 disabled:text-surface-white-300 flex cursor-pointer select-none items-center justify-center gap-2 transition-all disabled:cursor-not-allowed disabled:border disabled:border-solid',
    iconSlot: 'material-symbols-rounded',
    iconWrapperSlot: 'flex'
  },
  variants: {
    variant: {
      primary: {
        containerSlot:
          'bg-primary text-on-primary hover:bg-primary-400 active:bg-primary-500'
      },
      secondary: {
        containerSlot:
          'bg-surface-white-50 text-surface-black-50 border-surface-white-300 hover:border-surface-white-400 active:border-surface-white-300 active:bg-surface-white-200 border border-solid'
      },
      tertiary: {
        containerSlot:
          'text-surface-black-50 border-surface-white-200 hover:bg-surface-white-100 hover:border-surface-white-100 active:border-surface-white-300 active:bg-surface-white-100 border border-solid bg-transparent'
      },
      ghost: {
        containerSlot:
          'text-surface-black-50 hover:bg-surface-white-100 active:bg-surface-white-200 border-none bg-transparent'
      }
    },
    size: {
      lg: {
        containerSlot: 'text-body-xl rounded-2xl px-12 py-4 font-normal',
        iconWrapperSlot: 'text-icon-xl'
      },
      md: {
        containerSlot: 'text-body-md rounded-lg px-8 py-2 font-semibold',
        iconWrapperSlot: 'text-icon-xmd'
      },
      sm: {
        containerSlot: 'text-caption-lg rounded-lg px-6 py-1 font-semibold',
        iconWrapperSlot: 'text-body-xl'
      },
      xs: {
        containerSlot: 'text-caption-sm rounded-lg px-6 py-0.5 font-bold',
        iconWrapperSlot: 'text-icon-xs'
      },
      icon: {
        containerSlot: 'text-caption-lg rounded-lg px-2 py-1 font-semibold',
        iconWrapperSlot: 'text-body-xl'
      }
    },
    iconPosition: {
      left: {},
      right: {
        containerSlot: 'flex-row-reverse'
      }
    }
  },
  defaultVariants: {
    variant: 'primary',
    size: 'lg'
  }
})

export type TButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    icon?: string
    brandColor?: TColorProps
  }

/**
 * @deprecated Use `Button` from `@alternative/acqua/atoms` instead. The acqua-ui Button is being phased out in favor of the acqua design system.
 */
export const Button = forwardRef<HTMLButtonElement, TButtonProps>(
  (
    {
      children,
      disabled,
      icon,
      iconPosition = 'left',
      size,
      variant,
      brandColor,
      style,
      className,
      ...props
    },
    ref
  ) => {
    const { containerSlot, iconSlot, iconWrapperSlot } = buttonVariants({
      iconPosition,
      size,
      variant
    })

    const getBrandColorStyles = (): CSSProperties | undefined => {
      if (!brandColor || (variant !== 'primary' && variant) || disabled)
        return undefined

      const baseStyles: CSSProperties = {
        background: brandColor.hex,
        color: brandColor.isDark ? '#FFFFFF' : '#1A1A1A'
      }

      return baseStyles
    }

    const brandColorStyles = getBrandColorStyles()

    return (
      <button
        className={cn(containerSlot(), className)}
        ref={ref}
        disabled={disabled}
        style={{
          ...brandColorStyles,
          ...style
        }}
        onMouseEnter={e => {
          if (brandColor && (variant === 'primary' || !variant) && !disabled) {
            const hoverColor = brandColor.color.darken(0.2).hex()
            e.currentTarget.style.background = hoverColor
          }
        }}
        onMouseLeave={e => {
          if (brandColor && (variant === 'primary' || !variant) && !disabled) {
            e.currentTarget.style.background = brandColor.hex
          }
        }}
        {...props}
      >
        {icon && (
          <span className={iconWrapperSlot()}>
            <span className={iconSlot()}>{icon}</span>
          </span>
        )}
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'
